import { useRuntimeConfig } from '#imports'

const COOKIE_NAME = 'uia_consent'
const DEFAULT_MAX_AGE = 180 * 24 * 60 * 60 // 6 months

export const useConsentStore = defineStore('consent', () => {
  const config = useRuntimeConfig()
  const consentVersion = config.public?.consentVersion || '2025-01'

  const consentCookie = useCookie(COOKIE_NAME, {
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    maxAge: DEFAULT_MAX_AGE,
    path: '/'
  })

  const categories = ref({
    necessary: true,
    analytics: false,
    marketing: false
  })
  const initialized = ref(false)
  const decision = ref(null) // accept_all | reject_all | custom
  const savedAt = ref(null)
  const version = ref(consentVersion)
  const bannerVisible = ref(true)
  const preferencesVisible = ref(false)
  const ready = ref(false)

  function hydrateFromCookie() {
    if (!consentCookie.value) {
      version.value = consentVersion
      return
    }

    try {
      const parsed = typeof consentCookie.value === 'string' ? JSON.parse(consentCookie.value) : consentCookie.value
      categories.value = {
        necessary: true,
        analytics: !!parsed?.categories?.analytics,
        marketing: !!parsed?.categories?.marketing
      }
      decision.value = parsed?.decision || null
      savedAt.value = parsed?.savedAt || null
      version.value = parsed?.version || consentVersion
    } catch (error) {
      console.warn('Failed to parse consent cookie, resetting.', error)
      resetConsent()
    }
  }

  function persist() {
    const payload = {
      version: version.value,
      savedAt: savedAt.value,
      decision: decision.value,
      categories: {
        necessary: true,
        analytics: !!categories.value.analytics,
        marketing: !!categories.value.marketing
      }
    }
    consentCookie.value = payload
  }

  function resetConsent() {
    categories.value = { necessary: true, analytics: false, marketing: false }
    decision.value = null
    savedAt.value = null
    version.value = consentVersion
    bannerVisible.value = true
    preferencesVisible.value = false
    consentCookie.value = undefined
  }

  function setSavedNow(decisionType = 'custom') {
    decision.value = decisionType
    savedAt.value = new Date().toISOString()
    version.value = consentVersion
    bannerVisible.value = false
  }

  function acceptAll() {
    categories.value = { necessary: true, analytics: true, marketing: true }
    setSavedNow('accept_all')
    persist()
    preferencesVisible.value = false
  }

  function rejectAll() {
    categories.value = { necessary: true, analytics: false, marketing: false }
    setSavedNow('reject_all')
    persist()
    preferencesVisible.value = false
  }

  function savePreferences(payload) {
    categories.value = {
      necessary: true,
      analytics: !!payload.analytics,
      marketing: !!payload.marketing
    }
    setSavedNow('custom')
    persist()
    preferencesVisible.value = false
  }

  function openPreferences() {
    preferencesVisible.value = true
  }

  function closePreferences() {
    preferencesVisible.value = false
  }

  const needsPrompt = computed(() => !savedAt.value || version.value !== consentVersion)
  const analyticsAllowed = computed(() => categories.value.analytics === true)
  const marketingAllowed = computed(() => categories.value.marketing === true)
  const hasAnswered = computed(() => !!savedAt.value)

  function initialize() {
    if (!import.meta.client || initialized.value) return
    hydrateFromCookie()
    if (!needsPrompt.value) {
      bannerVisible.value = false
    }
    ready.value = true
    initialized.value = true
  }

  watch(
    () => categories.value,
    () => {
      if (!ready.value) return
      persist()
    },
    { deep: true }
  )

  return {
    categories,
    decision,
    savedAt,
    version,
    bannerVisible,
    preferencesVisible,
    ready,
    needsPrompt,
    analyticsAllowed,
    marketingAllowed,
    hasAnswered,
    hydrateFromCookie,
    persist,
    resetConsent,
    initialize,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences
  }
})
